import { Hash } from 'lucide-react';
import React, { useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { FaEdit, FaTrash, FaWhatsapp } from 'react-icons/fa';
import { Button } from '../../app/components';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { updateReserva } from '../../services/firestoreService';
import { Cliente, Reserva } from '../../types';
import { formatPrice } from '../../utils/priceUtils';
import { formatNumeroReserva } from '../../utils/reservaUtils';
import { openWhatsApp } from '../../utils/whatsappUtils';
import ConfirmModal from '../Common/ConfirmModal';
import DetailsPaymentsBooking from './Components/DetailsPaymentsBooking';
import { RegisterWithdrawal } from './Components/RegisterWithdrawal';
import { RegisterReturn } from './Components/RegisterReturn';
import RelatorioButton from './RelatorioButton';

interface ReservaViewModalProps {
  show: boolean;
  onHide: () => void;
  reserva: Reserva | null;
  cliente: Cliente | null;
  onEdit: (reserva: Reserva) => void;
  onDelete: (id: string) => void;
  onStatusChange?: () => void;
}

const ReservaViewModal: React.FC<ReservaViewModalProps> = ({
  show,
  onHide,
  reserva,
  cliente,
  onEdit,
  onDelete,
  onStatusChange
}) => {
  const [loading, setLoading] = useState(false);
  const { modalState, showConfirm, hideConfirm, handleConfirm } = useConfirmModal();

  if (!reserva) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Adicione esta nova função para formatar data e hora
  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return 'Data não disponível';

    const dateObj = date instanceof Date ? date : new Date(date);

    return dateObj.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: { [key: string]: { variant: string; text: string } } = {
      'reservado': { variant: 'warning', text: 'Reservado' },
      'retirado': { variant: 'info', text: 'Retirado' },
      'devolvido': { variant: 'success', text: 'Devolvido' },
      'cancelado': { variant: 'danger', text: 'Cancelado' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const getStatusPagamentoBadge = (status?: string) => {
    if (!status) return null;

    const statusConfig: { [key: string]: { variant: string; text: string } } = {
      'pago': { variant: 'success', text: 'Pago' },
      'pendente': { variant: 'warning', text: 'Pendente' },
      'parcial': { variant: 'info', text: 'Parcial' }
    };

    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const handleDelete = () => {
    showConfirm(
      'Excluir Reserva',
      `Tem certeza que deseja excluir a reserva do cliente ${reserva.clienteNome || 'não identificado'}? Esta ação não pode ser desfeita.`,
      () => {
        onDelete(reserva.id!);
        hideConfirm();
        onHide();
      },
      'danger',
      'Sim, Excluir',
      'Cancelar'
    );
  };

  const handleEdit = () => {
    onEdit(reserva);
    onHide();
  };

  const handleStatusChange = async (novoStatus: string) => {
    if (!reserva.id) return;

    setLoading(true);
    try {
      const result = await updateReserva(reserva.id, { status: novoStatus as any });
      if (result.success) {
        if (onStatusChange) {
          onStatusChange();
        }
      } else {
        alert('Erro ao alterar status: ' + result.error);
      }
    } catch (error) {
      alert('Erro ao alterar status da reserva');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      className="reserva-view-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="text-truncate">Detalhes da Reserva</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Cliente e Reserva */}
        <Row className="gy-3 mb-4">
          <Col xs={12} md={6}>
            <Card className="h-100">
              <Card.Header className="bg-primary text-white">
                <h6 className="mb-0">Informações do Cliente</h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Nome:</strong> {reserva.clienteNome || 'Cliente não encontrado'}
                </div>
                {cliente && (
                  <>
                    <div className="mb-2"><strong>Telefone:</strong> {cliente.telefone}</div>
                    <div className="mb-2"><strong>CPF:</strong> {cliente.cpf}</div>
                    {cliente.medidas && <div className="mb-2"><strong>Medidas:</strong> {cliente.medidas}</div>}
                    {cliente.observacoes && <div><strong>Observações:</strong> {cliente.observacoes}</div>}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={6}>
            <Card className="h-100">
              <Card.Header className="bg-info text-white">
                <h6 className="mb-0">Informações da Reserva</h6>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Número da Reserva: <Hash size={16} className="text-primary" /> <span className="fw-semibold text-primary" style={{ fontFamily: 'monospace' }}>
                    {formatNumeroReserva(reserva.numeroReserva || '')}
                  </span></strong>
                  <div className="d-flex align-items-center gap-2 mt-1">

                  </div>
                </div>
                <div className="mb-2">
                  <strong>Data de Criação da Reserva:</strong> {formatDateTime(reserva.createdAt)}
                </div>
                <div className="mb-2">
                  <strong>Data de Retirada:</strong> {formatDate(reserva.dataRetirada)}
                </div>
                {reserva.funcionarioNome && (
                  <div className="mb-2">
                    <strong>Atendido por:</strong> {reserva.funcionarioNome}
                  </div>
                )}
                <div className="mb-2">
                  <strong>Status:</strong> {getStatusBadge(reserva.status)}
                </div>


                {reserva.statusPagamento && (
                  <div className="mb-2"><strong>Status Pagamento:</strong> {getStatusPagamentoBadge(reserva.statusPagamento)}</div>
                )}
                {/* {reserva.valorTotal && reserva.valorTotal > 0 && (
                  <div className="mb-2"><strong>Valor Total:</strong> {formatPrice(reserva.valorTotal)}</div>
                )} */}
                {reserva.valorPago && reserva.valorPago > 0 && (
                  <div><strong>Valor Pago:</strong> {formatPrice(reserva.valorPago)}</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Produtos */}
        <Row className="gy-3">
          <Col xs={12}>
            <Card>
              <Card.Header className="bg-success text-white">
                <h6 className="mb-0">Produtos Reservados</h6>
              </Card.Header>
              <Card.Body>
                {reserva.produtos && reserva.produtos.length > 0 ? (
                  <>
                    {reserva.produtos.map((produto, index) => (
                      <div
                        key={index}
                        className="border rounded-3 p-3 mb-3 shadow-sm bg-light-subtle"
                      >
                        {/* Cabeçalho do produto */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <h6 className="mb-0 text-dark fw-semibold">
                              {produto.produtoTitulo}
                            </h6>
                          </div>

                          {/* Quantidade mais destacada */}
                          <span className="badge bg-primary-subtle text-primary px-3 py-2">
                            Quantidade: {produto.quantidade}
                          </span>
                        </div>

                        {/* Informações secundárias */}
                        <div className="text-muted small mb-2">
                          {produto.produtoTamanho && (
                            <div>
                              <strong>Tamanho:</strong> {produto.produtoTamanho}
                            </div>
                          )}
                          {produto.produtoCor && (
                            <div>
                              <strong>Cor:</strong> {produto.produtoCor}
                            </div>
                          )}
                          {produto.produtoCodigoProduto && (
                            <div>
                              <strong>Código:</strong> {produto.produtoCodigoProduto}
                            </div>
                          )}
                          {produto.produtoObservacoes && (
                            <div className="mt-2 p-2 bg-warning-subtle border border-warning rounded">
                              <strong>Observações do produto:</strong> {produto.produtoObservacoes}
                            </div>
                          )}
                        </div>

                        {/* Valores */}
                        <div className="mt-2">
                          {produto.precoUnitario !== undefined && (
                            <div className="text-success fw-semibold">
                              Preço Unitário: {formatPrice(produto.precoUnitario)}
                            </div>
                          )}
                          {produto.precoTotal !== undefined && (
                            <div className="text-primary fw-semibold">
                              Subtotal: {formatPrice(produto.precoTotal)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Total */}
                    {reserva.valorTotal && (
                      <div className="mt-4 pt-3 border-top text-center">
                        <h5 className="text-primary mb-0 fw-bold">
                          Total da Reserva: {formatPrice(reserva.valorTotal)}
                        </h5>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-muted text-center py-4">
                    Nenhum produto encontrado
                  </div>
                )}
              </Card.Body>

            </Card>
          </Col>
        </Row>

        {/* Observações */}
        {reserva.observacoes && (
          <Row className="mt-3">
            <Col xs={12}>
              <Card>
                <Card.Header className="bg-warning text-dark">
                  <h6 className="mb-0">Observações</h6>
                </Card.Header>
                <Card.Body>
                  <p className="mb-0">{reserva.observacoes}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Adicionar o componente de pagamentos */}
        <DetailsPaymentsBooking
          pagamentosData={reserva.pagamentosData} // Se existir
          valorTotal={reserva.valorTotal}
          valorPago={reserva.valorPago}
          statusPagamento={reserva.statusPagamento}
           retiradaDescontoPercentual={reserva.retiradaDescontoPercentual}
          retiradaDescontoValor={reserva.retiradaDescontoValor}
        />

      </Modal.Body>

      <Modal.Footer className="d-flex flex-column flex-sm-row gap-2">


        <Button btnColor="secondary" onClick={onHide}>Fechar</Button>
        {cliente && reserva.produtos && reserva.produtos.length > 0 && (
          <Button
            btnColor="outline-success"
            onClick={() => openWhatsApp(cliente.telefone, reserva.id || '', cliente.nome)}
            extraCss="d-flex align-items-center gap-2"
          >
            <FaWhatsapp size={20}/>
            WhatsApp
          </Button>
        )}

        {cliente && (
          <RelatorioButton
            reserva={reserva}
            cliente={cliente}
            className="d-flex align-items-center gap-2"
          />
        )}

        <Button
          btnColor="primary"
          onClick={handleEdit}
          extraCss="d-flex align-items-center gap-2"
        ><FaEdit size={20} />
          Editar
        </Button>

        <Button
          btnColor="danger"
          onClick={handleDelete}
          extraCss="d-flex align-items-center gap-2"
        ><FaTrash size={20}/>
          Excluir
        </Button>
        <RegisterWithdrawal
          reserva={reserva}
          onStatusChange={onStatusChange}
          onClose={onHide}
        />
        <RegisterReturn
          reserva={reserva}
          onStatusChange={onStatusChange}
          onClose={onHide}
        />
      </Modal.Footer>

      <ConfirmModal
        show={modalState.show}
        onHide={hideConfirm}
        onConfirm={handleConfirm}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
        loading={modalState.loading}
      />
    </Modal>
  );
};

export default ReservaViewModal;
